import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CreatableSelect from 'react-select/creatable';
import Modal from '../components/Modal';
import { header } from '../components/header.encry';

const typeOptions = [
  { value: 'category', label: 'category' },
  { value: 'tags', label: 'tags' },
  { value: 'prompts', label: 'prompts' },
  { value: 'globalmessage', label: 'globalmessage' },
  { value: 'stock', label: 'stock' },
  { value: 'items', label: 'items' },
  { value: 'product', label: 'product' }
];

const initialForm = { name: '', value: '', src: null, type: '', existingSrc: '' };

const hideBootstrapModal = (modalId) => {
  try {
    const modalElement = document.getElementById(modalId);
    const bs = window.bootstrap;

    if (bs && modalElement) {
      const instance = bs.Modal.getInstance(modalElement) || new bs.Modal(modalElement);
      instance.hide();
    }
  } catch (err) {
    console.error(`Unable to close modal ${modalId}`, err);
  }
};

const VariablesManager = () => {
  const [variables, setVariables] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [viewingVariable, setViewingVariable] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const isAllowed = roles.includes('admin') || roles.includes('manager');

  const fetchVariables = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_PEGASUS_API_URL}/variables`, header());
      setVariables(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching variables', err);
      toast.error('Erreur lors du chargement des variables');
    }
  }, []);

  useEffect(() => {
    if (isAllowed) {
      fetchVariables();
    }
  }, [fetchVariables, isAllowed]);

  const resetForm = () => {
    setEditingId(null);
    setFormData(initialForm);
  };

  const openAdd = () => {
    resetForm();
  };

  const openEdit = (variable) => {
    setEditingId(variable._id || variable.id);
    setFormData({
      name: variable.name || '',
      value: variable.value || '',
      src: null,
      type: variable.type || '',
      existingSrc: variable.src || ''
    });
  };

  const openView = (variable) => {
    setViewingVariable(variable);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFormData((prev) => ({ ...prev, src: file || null }));
  };

  const handleTypeChange = (selected) => {
    const value = selected ? selected.value : '';
    setFormData((prev) => {
      const updated = { ...prev, type: value };

      if (value === 'globalmessage') {
        updated.name = 'globalmessage';
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Veuillez renseigner un nom');
      return;
    }

    if (!formData.type) {
      toast.error('Veuillez selectionner un type');
      return;
    }

    const data = new FormData();
    data.append('name', formData.name.trim());
    data.append('value', formData.value || '');
    data.append('type', formData.type);

    if (formData.src) {
      data.append('src', formData.src);
    }

    setIsSaving(true);

    try {
      if (editingId) {
        await axios.put(
          `${process.env.REACT_APP_PEGASUS_API_URL}/variables/${editingId}`,
          data,
          header({ 'Content-Type': 'multipart/form-data' })
        );
        toast.success('Variable mise a jour avec succes');
      } else {
        await axios.post(
          `${process.env.REACT_APP_PEGASUS_API_URL}/variables`,
          data,
          header({ 'Content-Type': 'multipart/form-data' })
        );
        toast.success('Variable ajoutee avec succes');
      }

      await fetchVariables();
      hideBootstrapModal('variableModal');
      resetForm();
    } catch (err) {
      console.error('Save variable error', err);
      toast.error("Erreur lors de l'enregistrement de la variable");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression ?')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_PEGASUS_API_URL}/variables/${id}`, header());
      await fetchVariables();
      toast.success('Variable supprimee avec succes');
    } catch (err) {
      console.error('Delete variable error', err);
      toast.error('Erreur lors de la suppression de la variable');
    }
  };

  const filteredVariables = variables.filter((variable) => {
    const name = `${variable.name || ''}`.toLowerCase();
    const value = `${variable.value || ''}`.toLowerCase();
    const src = `${variable.src || ''}`.toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      name.includes(query) ||
      value.includes(query) ||
      src.includes(query);
    const matchesType = !typeFilter || variable.type === typeFilter.value;

    return matchesSearch && matchesType;
  });

  if (!isAllowed) {
    return <div>Acces non autorise</div>;
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Variables</h2>
        <button
          className="btn btn-success"
          onClick={openAdd}
          data-bs-toggle="modal"
          data-bs-target="#variableModal"
        >
          Ajouter
        </button>
      </div>

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Rechercher par nom, valeur ou src..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setSearchQuery('')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <CreatableSelect
            options={typeOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            isClearable
            placeholder="Filtrer par type..."
          />
        </div>
        <div className="col-md-2">
          <div className="text-muted">
            <small>{filteredVariables.length} resultat(s)</small>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Nom</th>
              <th style={{ width: '25%' }}>Valeur</th>
              <th style={{ width: '20%' }}>Src</th>
              <th style={{ width: '12%' }}>Type</th>
              <th style={{ width: '28%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVariables.length > 0 ? (
              filteredVariables.map((variable) => (
                <tr key={variable._id || variable.id}>
                  <td
                    style={{
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={variable.name}
                  >
                    {variable.name}
                  </td>
                  <td
                    style={{
                      maxWidth: '180px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={variable.value}
                  >
                    {variable.value}
                  </td>
                  <td
                    style={{
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={variable.src}
                  >
                    {variable.src}
                  </td>
                  <td>{variable.type}</td>
                  <td>
                    <button
                      className="btn btn-info btn-sm me-2"
                      onClick={() => openView(variable)}
                      data-bs-toggle="modal"
                      data-bs-target="#viewModal"
                    >
                      <i className="fas fa-eye"></i> Voir
                    </button>
                    <button
                      className="btn btn-primary btn-sm me-2"
                      onClick={() => openEdit(variable)}
                      data-bs-toggle="modal"
                      data-bs-target="#variableModal"
                    >
                      <i className="fas fa-edit"></i> Modifier
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(variable._id || variable.id)}
                    >
                      <i className="fas fa-trash"></i> Supprimer
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  <i className="fas fa-inbox fa-2x mb-2"></i>
                  <p>Aucune variable trouvee</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        modalId="variableModal"
        title={editingId ? 'Modifier la variable' : 'Ajouter une variable'}
        submitLabel={editingId ? 'Modifier' : 'Ajouter'}
        onSubmit={handleSubmit}
        onClose={resetForm}
        submitDisabled={isSaving}
      >
        <div className="mb-3">
          <label className="form-label">Nom</label>
          <input
            type="text"
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Valeur</label>
          <input
            type="text"
            className="form-control"
            name="value"
            value={formData.value}
            onChange={handleChange}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Src</label>
          {formData.existingSrc && (
            <div className="mb-2">
              <img
                src={`${process.env.REACT_APP_PEGASUS_API_URL}${formData.existingSrc}`}
                alt="variable"
                width={100}
              />
            </div>
          )}
          <input type="file" className="form-control" onChange={handleFileChange} />
        </div>
        <div className="mb-3">
          <label className="form-label">Type</label>
          <CreatableSelect
            options={typeOptions}
            value={formData.type ? { value: formData.type, label: formData.type } : null}
            onChange={handleTypeChange}
            isClearable
          />
        </div>
      </Modal>

      <div className="modal fade" id="viewModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Details de la variable</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {viewingVariable && (
                <div>
                  <div className="mb-3">
                    <strong>Nom:</strong>
                    <p
                      className="mt-1 p-2 bg-light border rounded"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {viewingVariable.name}
                    </p>
                  </div>
                  <div className="mb-3">
                    <strong>Valeur:</strong>
                    <pre
                      className="mt-1 p-2 bg-light border rounded"
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      {viewingVariable.value}
                    </pre>
                  </div>
                  <div className="mb-3">
                    <strong>Src:</strong>
                    {viewingVariable.src ? (
                      <div>
                        <p
                          className="mt-1 p-2 bg-light border rounded"
                          style={{ wordBreak: 'break-word' }}
                        >
                          {viewingVariable.src}
                        </p>
                        {/\.(jpg|jpeg|png|gif|webp)$/i.test(viewingVariable.src) && (
                          <img
                            src={`${process.env.REACT_APP_PEGASUS_API_URL}${viewingVariable.src}`}
                            alt="variable"
                            className="img-fluid mt-2"
                            style={{ maxHeight: '300px' }}
                          />
                        )}
                      </div>
                    ) : (
                      <p className="text-muted mt-1">Aucun fichier</p>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Type:</strong>
                    <p className="mt-1 p-2 bg-light border rounded">{viewingVariable.type}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariablesManager;
